/* eslint-disable max-lines */
/**
 * A2A v1.0 client helper for bex-platform-react.
 *
 * Driving a DE (SendMessage / SendStreamingMessage) goes through bex-server at
 * `/assistant/a2a/{org}/{sub}/{assistant}/…`. The DE's `assistant_token` is a
 * long-lived credential granting full execution rights, so the browser never
 * holds it: reads of the assistant doc redact it (they answer `has_token`), and
 * the server looks it up per call and injects the Bearer itself. The session
 * cookie is what authorises the browser.
 *
 * Task inspection (GetTask / CancelTask) and the agent card still address
 * Mind's execute plane directly through Envoy. GetTask/CancelTask are bearer-
 * authenticated by A2A, so they are callable only by a holder of the token —
 * not from a browser session. They are kept for server-side/test callers.
 *
 * Source of truth for the protocol:
 *   strategies/S-0001/actions/A-0007/A-0007.04/investigate/A2A-PROTOCOL.md
 *
 * Exports:
 *   - sendMessage(config, opts)         non-streaming assistant execution
 *   - sendStreamingMessage(config, opts) streaming SSE (returns async iterable)
 *   - getTask(config, opts)             fetch persisted task
 *   - cancelTask(config, opts)          request cancellation
 *   - fetchAgentCard(config)            GET discovery card (no auth)
 *   - buildPartsFromTypedInput(ti)      helper (exported for tests)
 *   - extractOutputFromTask(task)       helper (exported for tests)
 *   - extractErrorFromFailedTask(task)  helper (exported for tests)
 */

import {
 bexHttpClient
} from '../../clients';
import {
 projectOService
} from './projectOProxy.service';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type TypedInput = {
    context?: Record<string, any>;
    propensity?: Record<string, any>;
    intent?: string | Record<string, any> | null;
    entity?: Record<string, any>;
};

export type Part =
    | { type: 'text'; text: string; metadata?: Record<string, any> }
    | { type: 'raw'; raw: string; mediaType?: string; filename?: string; metadata?: Record<string, any> }
    | { type: 'url'; url: string; mediaType?: string; filename?: string; metadata?: Record<string, any> }
    | { type: 'data'; data: any; metadata?: Record<string, any> };

export type TaskState =
    | 'TASK_STATE_SUBMITTED'
    | 'TASK_STATE_WORKING'
    | 'TASK_STATE_COMPLETED'
    | 'TASK_STATE_FAILED'
    | 'TASK_STATE_CANCELED'
    | 'TASK_STATE_REJECTED'
    | 'TASK_STATE_INPUT_REQUIRED'
    | 'TASK_STATE_AUTH_REQUIRED';

export interface TaskStatus {
    state: TaskState;
    timestamp?: string;
    message?: {
        role: 'user' | 'agent';
        parts: Part[];
        messageId?: string;
        taskId?: string;
        contextId?: string;
    };
}

export interface Artifact {
    artifactId: string;
    parts: Part[];
    name?: string;
    metadata?: Record<string, any>;
}

export interface Task {
    id: string;
    contextId?: string;
    status: TaskStatus;
    history?: any[];
    artifacts?: Artifact[];
    metadata?: Record<string, any>;
}

export interface A2AConfig {
    orgId: string;
    subOrgId: string;
    assistantId: string;
    /**
     * The DE's A2A bearer. Never available to the browser (reads redact it) and
     * never needed there — sendMessage/sendStreamingMessage go through
     * bex-server, which injects it. Only token-holding callers (GetTask,
     * CancelTask) set this.
     */
    assistantToken?: string;
}

export interface BexStreamingExtensions {
    emitTokenEvents?: boolean;
    streamCumulative?: boolean;
    streamBufferSentences?: boolean;
}

export interface SendMessageOpts {
    typedInput: TypedInput;
    memoryLogId?: string;
    opcodeId?: string;
    channelId?: string;
    bex?: BexStreamingExtensions;
    requestId?: number;
    timeout?: number;
}

export interface SendMessageResult {
    task: Task | null;
    output: any | null;
    errors: string[];
    state: TaskState | 'UNKNOWN';
    taskId: string | null;
    jsonrpcError?: { code: number; message: string; reason: string };
}

export interface StreamEvent {
    jsonrpc: '2.0';
    id: number | string | null;
    result: any; // Task | TaskStatusUpdateEvent | TaskArtifactUpdateEvent | Message
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a BeX typed_input dict into an array of A2A Message Parts carrying
 * the BeX typed-input extension metadata (PROTOCOL §5.2).
 *
 *   context.<name>      -> TextPart or DataPart, metadata={category:context, name}
 *   propensity.<name>   -> same, category=propensity
 *   intent              -> single TextPart/DataPart, metadata={category:intent}
 *   entity.<name>       -> TextPart/DataPart, metadata={category:entity, name}
 */
export function buildPartsFromTypedInput(ti: TypedInput): Part[] {
    const parts: Part[] = [];
    const add = (category: string, name: string | null, value: any) => {
        const metadata: Record<string, any> = {
 category
};
        if (name !== null) metadata.name = name;
        if (typeof value === 'string') {
            parts.push({
 type: 'text',
text: value,
metadata
});
        } else if (value !== null && typeof value === 'object') {
            parts.push({
 type: 'data',
data: value,
metadata
});
        }
    };

    for (const [k, v] of Object.entries(ti.context ?? {})) add('context', k, v);
    for (const [k, v] of Object.entries(ti.propensity ?? {})) add('propensity', k, v);
    if (ti.intent !== null && ti.intent !== undefined) add('intent', null, ti.intent);
    for (const [k, v] of Object.entries(ti.entity ?? {})) add('entity', k, v);

    // Fallback — Pydantic requires parts to exist; an empty text part keeps
    // the Message valid even if the caller sent nothing.
    if (parts.length === 0) {
        parts.push({
            type: 'text',
            text: '',
            metadata: {
 category: 'context',
name: 'user_message'
},
        });
    }

    return parts;
}

/**
 * Extract the structured executor result from a completed Task.
 * Mind puts the full result into the execution_result artifact's DataPart
 * with metadata.source='executor_result'.
 */
export function extractOutputFromTask(task: Task | null): any | null {
    if (!task || !task.artifacts || task.artifacts.length === 0) return null;
    const artifact = task.artifacts.find((a) => a?.name === 'execution_result')
        ?? task.artifacts[0];
    if (!artifact?.parts) return null;

    const dataPart = artifact.parts.find(
        (p: any) => p.type === 'data' && p.metadata?.source === 'executor_result',
    ) as Extract<Part, { type: 'data' }> | undefined;
    if (dataPart?.data !== undefined) return dataPart.data;

    const anyData = artifact.parts.find((p: any) => p.type === 'data') as
        | Extract<Part, { type: 'data' }>
        | undefined;
    if (anyData?.data !== undefined) return anyData.data;

    const textPart = artifact.parts.find((p: any) => p.type === 'text') as
        | Extract<Part, { type: 'text' }>
        | undefined;
    if (typeof textPart?.text === 'string') return {
 text: textPart.text
};

    return null;
}

export function extractErrorFromFailedTask(task: Task | null): string | null {
    if (!task?.status?.message?.parts) return null;
    const textPart = task.status.message.parts.find((p: any) => p.type === 'text') as
        | Extract<Part, { type: 'text' }>
        | undefined;
    return typeof textPart?.text === 'string' ? textPart.text : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// URL + envelope builders
// ─────────────────────────────────────────────────────────────────────────────

function rpcUrl(cfg: A2AConfig): string {
    const o = encodeURIComponent(cfg.orgId);
    const s = encodeURIComponent(cfg.subOrgId);
    const a = encodeURIComponent(cfg.assistantId);
    return `projecto/execute/api/v1/a2a/${o}/${s}/${a}/rpc`;
}

function agentCardUrl(cfg: Omit<A2AConfig, 'assistantToken'>): string {
    const o = encodeURIComponent(cfg.orgId);
    const s = encodeURIComponent(cfg.subOrgId);
    const a = encodeURIComponent(cfg.assistantId);
    return `projecto/execute/api/v1/a2a/${o}/${s}/${a}/.well-known/agent-card.json`;
}

/**
 * bex-server's credential-injecting A2A surface. `leaf` is 'message'
 * (SendMessage -> JSON) or 'stream' (SendStreamingMessage -> SSE); the JSON-RPC
 * method is a property of the route, not of the body.
 */
function proxyUrl(cfg: A2AConfig, leaf: 'message' | 'stream'): string {
    const o = encodeURIComponent(cfg.orgId);
    const s = encodeURIComponent(cfg.subOrgId);
    const a = encodeURIComponent(cfg.assistantId);
    return `/assistant/a2a/${o}/${s}/${a}/${leaf}`;
}

/**
 * The proxy takes typed_input flat in the body and builds the A2A envelope
 * server-side, so the browser never hand-rolls a JSON-RPC frame it isn't
 * authorised to address.
 */
function buildProxyBody(opts: SendMessageOpts): Record<string, any> {
    const ti = opts.typedInput ?? {};
    const body: Record<string, any> = {
        context: ti.context ?? {},
        propensity: ti.propensity ?? {},
        intent: ti.intent ?? null,
        entity: ti.entity ?? {},
    };
    if (opts.memoryLogId) body.session_id = opts.memoryLogId;
    if (opts.opcodeId) body.opcode_id = opts.opcodeId;
    if (opts.channelId) body.channel_id = opts.channelId;
    if (opts.bex && Object.keys(opts.bex).length > 0) body.bex = opts.bex;
    return body;
}

function interpretEnvelope(envelope: any, requestedState: TaskState[] = ['TASK_STATE_COMPLETED']): SendMessageResult {
    if (!envelope || typeof envelope !== 'object') {
        return {
 task: null,
output: null,
errors: ['A2A response envelope is not an object'],
state: 'UNKNOWN',
taskId: null
};
    }
    if (envelope.error) {
        const err = envelope.error;
        const reasonObj = Array.isArray(err.data)
            ? err.data.find((d: any) => d?.['@type']?.endsWith('/google.rpc.ErrorInfo'))
            : null;
        const reason = reasonObj?.reason ?? `code_${err.code}`;
        return {
            task: null,
            output: null,
            errors: [`${err.message ?? 'A2A error'} (${reason})`],
            state: 'UNKNOWN',
            taskId: null,
            jsonrpcError: {
 code: err.code,
message: err.message,
reason
},
        };
    }
    const task = envelope.result as Task;
    const state = (task?.status?.state ?? 'UNKNOWN') as TaskState | 'UNKNOWN';
    if (requestedState.includes(state as TaskState)) {
        return {
 task,
output: extractOutputFromTask(task),
errors: [],
state,
taskId: task.id
};
    }
    const errMsg = extractErrorFromFailedTask(task);
    return {
        task,
        output: null,
        errors: errMsg ? [errMsg] : [`Task ended in state ${state}`],
        state,
        taskId: task?.id ?? null,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public methods
// ─────────────────────────────────────────────────────────────────────────────

export async function sendMessage(cfg: A2AConfig, opts: SendMessageOpts): Promise<SendMessageResult> {
    // No Authorization header: bex-server resolves the DE's token from the
    // assistant doc on every call and forwards Mind's envelope back verbatim.
    let envelope: any;
    try {
        const response = await bexHttpClient.post(proxyUrl(cfg, 'message'), buildProxyBody(opts), {
            timeout: opts.timeout ?? 120000,
        });
        envelope = response?.data;
    } catch (err: any) {
        // Mind's own refusals (bad params, DE error) keep their status and carry
        // a JSON-RPC error envelope — that is a result to interpret, not a
        // transport failure. Anything else is a genuine failure to reach Mind.
        const data = err?.response?.data;
        if (data && typeof data === 'object' && ('error' in data || 'result' in data)) {
            envelope = data;
        } else {
            const message = data?.message ?? err?.message ?? 'A2A SendMessage failed';
            return {
                task: null,
output: null,
errors: [message],
state: 'UNKNOWN',
taskId: null,
            };
        }
    }
    return interpretEnvelope(envelope);
}

export async function getTask(
    cfg: A2AConfig,
    opts: { taskId: string; historyLength?: number; requestId?: number; timeout?: number },
): Promise<SendMessageResult> {
    if (!cfg.assistantToken) {
        throw new Error('a2aClient.getTask: assistantToken required');
    }
    const params: Record<string, any> = {
 id: opts.taskId
};
    if (opts.historyLength !== undefined) params.historyLength = opts.historyLength;
    const body = {
 jsonrpc: '2.0',
id: opts.requestId ?? Date.now(),
method: 'GetTask',
params
};
    const response = await projectOService.performRequest(rpcUrl(cfg), {
        method: 'POST',
        data: body,
        timeout: opts.timeout ?? 30000,
        headers: {
            'Content-Type': 'application/a2a+json',
            Authorization: `Bearer ${cfg.assistantToken}`,
        },
    } as any);
    return interpretEnvelope(
        response,
        ['TASK_STATE_COMPLETED', 'TASK_STATE_WORKING', 'TASK_STATE_SUBMITTED', 'TASK_STATE_CANCELED', 'TASK_STATE_FAILED', 'TASK_STATE_REJECTED'],
    );
}

export async function cancelTask(
    cfg: A2AConfig,
    opts: { taskId: string; requestId?: number; timeout?: number },
): Promise<SendMessageResult> {
    if (!cfg.assistantToken) {
        throw new Error('a2aClient.cancelTask: assistantToken required');
    }
    const body = {
        jsonrpc: '2.0',
        id: opts.requestId ?? Date.now(),
        method: 'CancelTask',
        params: {
 id: opts.taskId
},
    };
    const response = await projectOService.performRequest(rpcUrl(cfg), {
        method: 'POST',
        data: body,
        timeout: opts.timeout ?? 30000,
        headers: {
            'Content-Type': 'application/a2a+json',
            Authorization: `Bearer ${cfg.assistantToken}`,
        },
    } as any);
    return interpretEnvelope(response, ['TASK_STATE_CANCELED']);
}

export async function fetchAgentCard(
    cfg: Omit<A2AConfig, 'assistantToken'>,
    opts: { timeout?: number } = {},
): Promise<any> {
    // Public endpoint — no auth. GET via the proxy.
    const response = await projectOService.performRequest(agentCardUrl(cfg), {
        method: 'GET',
        timeout: opts.timeout ?? 30000,
    } as any);
    return response;
}

/**
 * Stream an assistant execution via A2A SendStreamingMessage (SSE).
 *
 * Returns an async iterable yielding StreamEvent objects (already parsed
 * JSON-RPC envelopes containing result=Task|TaskStatusUpdateEvent|
 * TaskArtifactUpdateEvent|Message per PROTOCOL §5.7).
 *
 * Terminal event has result.final === true.
 *
 * Implementation note: axios doesn't expose a native streaming reader in the
 * browser, so this uses fetch directly and reads the response body.
 * bexHttpClient.defaults.baseURL tells us the server URL; credentials:'include'
 * carries the bex session cookie, which is the only auth the browser supplies —
 * bex-server injects the DE's Bearer and pipes Mind's SSE frames untouched.
 */
export async function* sendStreamingMessage(
    cfg: A2AConfig,
    opts: SendMessageOpts,
): AsyncIterable<StreamEvent> {
    const baseURL = (bexHttpClient.defaults.baseURL ?? '').replace(/\/$/, '');

    const response = await fetch(`${baseURL}${proxyUrl(cfg, 'stream')}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildProxyBody(opts)),
    });

    if (!response.ok || !response.body) {
        // A refusal before the stream opens comes back as JSON (bex-server
        // drains it rather than opening an SSE channel that carries only an
        // error) — surface its message instead of a bare status code.
        let detail = '';
        try {
            const text = await response.text();
            const parsed = text ? JSON.parse(text) : null;
            detail = parsed?.message ?? parsed?.error?.message ?? text ?? '';
        } catch { /* keep the status-only message */ }
        throw new Error(`A2A stream open failed: HTTP ${response.status}${detail ? ` — ${detail}` : ''}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
        while (true) {
            const {
 done, value
} = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, {
 stream: true
});
            // SSE frames separated by blank line
            let sep: number;
            while ((sep = buffer.indexOf('\n\n')) !== -1) {
                const frame = buffer.slice(0, sep);
                buffer = buffer.slice(sep + 2);
                // Strip leading "data: "
                const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
                if (!dataLine) continue;
                const payload = dataLine.slice(5).trim();
                if (!payload) continue;
                try {
                    yield JSON.parse(payload) as StreamEvent;
                } catch {
                    // skip malformed frame
                }
            }
        }
    } finally {
        try { reader.releaseLock(); } catch { /* ignore */ }
    }
}
